import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Users, Plus, Trash2, RefreshCw } from 'lucide-react';
import { getV2Directors, createV2Director, deleteV2Director } from '@/api/v2-deployment';
import type { V2Director } from '@/types/v2-deployment';

interface V2DirectorsCardProps {
  schoolCode: string;
}

export function V2DirectorsCard({ schoolCode }: V2DirectorsCardProps) {
  const [directors, setDirectors] = useState<V2Director[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const fetchDirectors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getV2Directors(schoolCode);
      setDirectors(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erreur lors du chargement des directeurs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [schoolCode]);

  useEffect(() => {
    fetchDirectors();
  }, [fetchDirectors]);

  const handleCreate = async () => {
    if (!firstname.trim() || !lastname.trim() || !email.trim()) {
      setFormError('Tous les champs sont requis');
      return;
    }

    try {
      setCreating(true);
      setFormError(null);
      await createV2Director(schoolCode, {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim(),
      });
      setDialogOpen(false);
      setFirstname('');
      setLastname('');
      setEmail('');
      await fetchDirectors();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (director: V2Director) => {
    const name = `${director.firstname} ${director.lastname}`;
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le directeur ${name} ?`)) {
      return;
    }

    try {
      setDeleting(director.id);
      await deleteV2Director(schoolCode, director.id);
      await fetchDirectors();
    } catch (err) {
      console.error('Error deleting director:', err);
      alert('Erreur lors de la suppression: ' + (err as Error).message);
    } finally {
      setDeleting(null);
    }
  };

  const openDialog = () => {
    setFirstname('');
    setLastname('');
    setEmail('');
    setFormError(null);
    setDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader
          icon={<Users />}
          iconColor="purple"
          actions={
            <Button size="sm" onClick={openDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Ajouter un directeur
            </Button>
          }
        >
          <CardTitle>Directeurs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Chargement...</span>
            </div>
          ) : error ? (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
              <Button variant="ghost" size="sm" className="ml-2" onClick={fetchDirectors}>
                Réessayer
              </Button>
            </div>
          ) : directors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>Aucun directeur</p>
              <p className="text-sm mt-1">
                Ajoutez un directeur pour qu'il puisse configurer son compte.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prénom</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {directors.map((director) => (
                  <TableRow key={director.id}>
                    <TableCell>{director.firstname}</TableCell>
                    <TableCell>{director.lastname}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {director.email || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          director.status === 'ACTIVE'
                            ? 'success'
                            : director.status === 'INACTIVE'
                              ? 'warning'
                              : 'default'
                        }
                      >
                        {director.status === 'ACTIVE'
                          ? 'Actif'
                          : director.status === 'INACTIVE'
                            ? 'Inactif'
                            : director.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(director)}
                        disabled={deleting === director.id}
                      >
                        {deleting === director.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Ajouter un directeur</DialogTitle>
            <DialogDescription>
              Le directeur recevra un email pour configurer son mot de passe et accéder à l'école.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {formError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {formError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="director-firstname">Prénom</Label>
              <Input
                id="director-firstname"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                placeholder="Prénom du directeur"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="director-lastname">Nom</Label>
              <Input
                id="director-lastname"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                placeholder="Nom du directeur"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="director-email">Email</Label>
              <Input
                id="director-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemple.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={creating}
            >
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
